export const introToProgrammingCourse = {
  id: 'intro-programming-101',
  title: 'Introduction to Programming',
  description: 'A comprehensive introduction to programming fundamentals using JavaScript, covering basic syntax, problem-solving techniques, and foundational computer science concepts.',
  credits: 4,
  duration: '15 weeks',
  level: 'Beginner',
  category: 'Coding',
  learningObjectives: [
    'Understand fundamental programming concepts including variables, data types, and operators',
    'Master control structures such as conditionals, loops, and functions',
    'Apply problem-solving techniques to break down complex problems into manageable parts',
    'Develop skills in debugging and testing code effectively',
    'Learn basic algorithms and data structures including arrays and objects',
    'Understand programming best practices and code organization principles',
    'Gain experience with version control and collaborative programming workflows',
    'Build practical programming projects demonstrating core concepts'
  ],
  prerequisites: ['Basic computer literacy', 'High school mathematics'],
  skills: [
    'JavaScript Programming',
    'Problem Solving',
    'Algorithmic Thinking',
    'Debugging Techniques',
    'Code Documentation',
    'Version Control (Git)',
    'Software Development Lifecycle',
    'Testing and Quality Assurance',
    'Data Structure Fundamentals',
    'Object-Oriented Programming Basics',
    'Web Development Foundations',
    'Computational Thinking'
  ],
  marketDemand: 'Very High',
  averageSalary: '$65,000 - $120,000',
  careerPaths: [
    'Software Developer',
    'Web Developer',
    'Frontend Developer',
    'Backend Developer',
    'Full-Stack Developer',
    'Mobile App Developer',
    'Quality Assurance Engineer',
    'DevOps Engineer',
    'Systems Analyst',
    'Technical Support Specialist',
    'Data Analyst',
    'IT Consultant'
  ],
  industryApplications: [
    'Web Development',
    'Mobile Application Development',
    'Software Engineering',
    'Game Development',
    'Data Science and Analytics',
    'Automation and Scripting',
    'Database Management',
    'Cloud Computing'
  ],
  certifications: [
    'JavaScript Developer Certification',
    'CompTIA IT Fundamentals+',
    'Microsoft Technology Associate (MTA)',
    'Oracle Certified Associate Java Programmer',
    'AWS Certified Developer',
    'Google Developer Certification'
  ],
  modules: [
    {
      id: 'module-1',
      title: 'Programming Fundamentals',
      description: 'Introduction to programming concepts, development environment setup, and basic JavaScript syntax',
      weeks: [1, 2, 3],
      learningOutcomes: [
        'Set up a programming development environment',
        'Understand variables, data types, and basic operations',
        'Write and execute simple JavaScript programs',
        'Apply proper syntax and code formatting conventions'
      ],
      lessons: [
        {
          id: 'lesson-1',
          title: 'Introduction to Programming and Setup',
          content: 'Welcome to programming! This lesson introduces the fundamental concepts of programming, explores what programmers do, and sets up your development environment. You will learn about programming languages, how computers execute code, and the role of JavaScript in modern software development. We will also cover essential tools including code editors, browsers, and developer tools.',
          duration: '120 minutes',
          readingMaterials: [
            'Introduction to Computer Programming - Chapter 1',
            'JavaScript: The Good Parts - Introduction',
            'Setting Up Your Development Environment Guide'
          ],
          keyTerms: [
            'Programming Language',
            'Source Code',
            'Compiler',
            'Interpreter',
            'IDE (Integrated Development Environment)',
            'Syntax',
            'Algorithm',
            'Debugging'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Write your first JavaScript program! Create a simple greeting program that displays "Hello, World!" to the console.',
              initialCode: '// Your first JavaScript program\n// Write code to display "Hello, World!" to the console\n\n',
              expectedOutput: 'Hello, World!',
              hints: [
                'Use console.log() to display text to the console',
                'Remember to put text inside quotes',
                'The exact text should be "Hello, World!" with proper capitalization'
              ]
            }
          }
        },
        {
          id: 'lesson-2',
          title: 'Variables and Data Types',
          content: 'Learn about variables as containers for storing data and explore different data types in JavaScript. This lesson covers primitive data types including numbers, strings, booleans, undefined, and null. You will understand variable declaration using let, const, and var keywords, naming conventions, and how JavaScript handles dynamic typing.',
          duration: '120 minutes',
          readingMaterials: [
            'JavaScript Variables and Data Types',
            'Understanding Dynamic Typing in JavaScript',
            'Variable Naming Conventions and Best Practices'
          ],
          keyTerms: [
            'Variable',
            'Data Type',
            'String',
            'Number',
            'Boolean',
            'Undefined',
            'Null',
            'Dynamic Typing',
            'Variable Declaration',
            'Variable Scope'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create variables to store information about yourself. Declare variables for your name (string), age (number), and whether you enjoy programming (boolean). Then use console.log to display a message using these variables.',
              initialCode: '// Create variables for personal information\n// Declare: name, age, enjoysProgramming\n// Display a message using these variables\n\n',
              expectedOutput: 'My name is John, I am 25 years old, and I enjoy programming: true',
              hints: [
                'Use let or const to declare variables',
                'Strings need to be in quotes, numbers do not',
                'Booleans are true or false (no quotes)',
                'Use template literals or string concatenation to create the message'
              ]
            }
          }
        },
        {
          id: 'lesson-3',
          title: 'Operators and Expressions',
          content: 'Explore JavaScript operators for performing calculations, comparisons, and logical operations. This lesson covers arithmetic operators, assignment operators, comparison operators, logical operators, and operator precedence. You will learn how to build complex expressions and understand how JavaScript evaluates different types of operations.',
          duration: '120 minutes',
          readingMaterials: [
            'JavaScript Operators Reference',
            'Understanding Operator Precedence',
            'Boolean Logic and Logical Operators'
          ],
          keyTerms: [
            'Arithmetic Operators',
            'Assignment Operators',
            'Comparison Operators',
            'Logical Operators',
            'Operator Precedence',
            'Expression',
            'Truthy and Falsy',
            'Type Coercion'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a simple calculator program. Define two numbers and calculate their sum, difference, product, and quotient. Display the results using console.log.',
              initialCode: '// Simple calculator\n// Define two numbers: num1 = 15, num2 = 4\n// Calculate and display sum, difference, product, quotient\n\n',
              expectedOutput: 'Sum: 19\nDifference: 11\nProduct: 60\nQuotient: 3.75',
              hints: [
                'Use + for addition, - for subtraction, * for multiplication, / for division',
                'Store results in variables before displaying them',
                'Use console.log for each calculation result',
                'Format the output as "Operation: result"'
              ]
            }
          }
        },
        {
          id: 'lesson-4',
          title: 'Input and Output Basics',
          content: 'Learn how to interact with users through input and output operations. This lesson covers console.log for output, prompt() for user input in browser environments, and basic string manipulation for formatting output. You will understand how to create interactive programs that respond to user input.',
          duration: '90 minutes',
          readingMaterials: [
            'User Input and Output in JavaScript',
            'String Manipulation Basics',
            'Interactive Programming Concepts'
          ],
          keyTerms: [
            'Input',
            'Output',
            'User Interface',
            'String Interpolation',
            'Template Literals',
            'User Experience',
            'Data Validation'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a program that greets a user by name. Set a variable called "userName" to "Alice" and display a personalized greeting message.',
              initialCode: '// User greeting program\n// Set userName variable and create personalized greeting\n\n',
              expectedOutput: 'Hello, Alice! Welcome to programming!',
              hints: [
                'Declare a variable userName with the value "Alice"',
                'Use template literals (backticks) or string concatenation',
                'The greeting should be exactly: "Hello, Alice! Welcome to programming!"'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Programming Assignment',
          title: 'Personal Information Display Program',
          points: 50,
          dueWeek: 2
        },
        {
          type: 'Quiz',
          title: 'Variables and Data Types Quiz',
          points: 25,
          dueWeek: 3
        }
      ]
    },
    {
      id: 'module-2',
      title: 'Control Structures',
      description: 'Master conditional statements and loops to control program flow and create more sophisticated programs',
      weeks: [4, 5, 6],
      learningOutcomes: [
        'Implement conditional logic using if-else statements',
        'Create complex decision-making structures with nested conditions',
        'Use loops to repeat code execution efficiently',
        'Apply control structures to solve practical programming problems'
      ],
      lessons: [
        {
          id: 'lesson-5',
          title: 'Conditional Statements',
          content: 'Learn how to make decisions in your programs using conditional statements. This lesson covers if statements, if-else statements, else-if chains, and the ternary operator. You will understand how to create branching logic that responds to different conditions and user inputs, making your programs more dynamic and interactive.',
          duration: '120 minutes',
          readingMaterials: [
            'Conditional Logic in Programming',
            'JavaScript If-Else Statements Guide',
            'Boolean Logic and Decision Making'
          ],
          keyTerms: [
            'Conditional Statement',
            'If Statement',
            'Else Statement',
            'Else If',
            'Ternary Operator',
            'Boolean Expression',
            'Nested Conditions',
            'Control Flow'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a grade evaluation program. Set a variable "score" to 85 and use if-else statements to determine and display the letter grade (A: 90+, B: 80-89, C: 70-79, D: 60-69, F: below 60).',
              initialCode: '// Grade evaluation program\n// Set score = 85 and determine letter grade\n\n',
              expectedOutput: 'Grade: B',
              hints: [
                'Use if-else if-else structure for multiple conditions',
                'Check conditions from highest to lowest grade',
                'A score of 85 should result in grade B',
                'Display the result as "Grade: B"'
              ]
            }
          }
        },
        {
          id: 'lesson-6',
          title: 'Switch Statements',
          content: 'Explore switch statements as an alternative to multiple if-else conditions when dealing with specific values. This lesson covers switch syntax, case statements, break statements, default cases, and when to use switch versus if-else. You will learn how to write cleaner, more readable code for multi-way branching scenarios.',
          duration: '120 minutes',
          readingMaterials: [
            'JavaScript Switch Statements',
            'When to Use Switch vs If-Else',
            'Control Flow Best Practices'
          ],
          keyTerms: [
            'Switch Statement',
            'Case',
            'Break Statement',
            'Default Case',
            'Fall-through',
            'Multi-way Branching',
            'Code Readability'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a day-of-week program using a switch statement. Set a variable "dayNumber" to 3 and display the corresponding day name (1=Monday, 2=Tuesday, 3=Wednesday, etc.).',
              initialCode: '// Day of week program\n// Set dayNumber = 3 and use switch to display day name\n\n',
              expectedOutput: 'Wednesday',
              hints: [
                'Use switch statement with dayNumber as the expression',
                'Create cases for 1-7 representing days Monday-Sunday',
                'Include break statements to prevent fall-through',
                'Day 3 should display "Wednesday"'
              ]
            }
          }
        },
        {
          id: 'lesson-7',
          title: 'Loops - For and While',
          content: 'Master repetitive execution using loops. This lesson covers while loops, do-while loops, for loops, and choosing the appropriate loop type for different scenarios. You will learn about loop conditions, loop counters, infinite loops, and how to use loops to process data efficiently.',
          duration: '120 minutes',
          readingMaterials: [
            'JavaScript Loops Tutorial',
            'Loop Types and Use Cases',
            'Avoiding Infinite Loops'
          ],
          keyTerms: [
            'Loop',
            'Iteration',
            'While Loop',
            'Do-While Loop',
            'For Loop',
            'Loop Counter',
            'Loop Condition',
            'Infinite Loop',
            'Break Statement',
            'Continue Statement'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a countdown program using a for loop. Start from 5 and count down to 1, displaying each number. After the countdown, display "Blast off!".',
              initialCode: '// Countdown program\n// Count from 5 down to 1, then display "Blast off!"\n\n',
              expectedOutput: '5\n4\n3\n2\n1\nBlast off!',
              hints: [
                'Use a for loop with i starting at 5',
                'Decrement i in each iteration (i--)',
                'Continue while i >= 1',
                'Use console.log to display each number and the final message'
              ]
            }
          }
        },
        {
          id: 'lesson-8',
          title: 'Nested Loops and Complex Patterns',
          content: 'Learn to create complex patterns and solve advanced problems using nested loops. This lesson covers nested loop structures, pattern printing, performance considerations, and practical applications of nested iterations. You will develop skills in visualizing and implementing complex algorithmic solutions.',
          duration: '120 minutes',
          readingMaterials: [
            'Nested Loops and Algorithm Design',
            'Pattern Programming Techniques',
            'Loop Performance and Optimization'
          ],
          keyTerms: [
            'Nested Loops',
            'Pattern Programming',
            'Algorithm Complexity',
            'Outer Loop',
            'Inner Loop',
            'Performance Optimization',
            'Time Complexity'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a multiplication table for numbers 1-3. Display each multiplication (e.g., "1 x 1 = 1") using nested loops.',
              initialCode: '// Multiplication table for 1-3\n// Use nested loops to display all combinations\n\n',
              expectedOutput: '1 x 1 = 1\n1 x 2 = 2\n1 x 3 = 3\n2 x 1 = 2\n2 x 2 = 4\n2 x 3 = 6\n3 x 1 = 3\n3 x 2 = 6\n3 x 3 = 9',
              hints: [
                'Use two nested for loops, both running from 1 to 3',
                'Outer loop variable represents the first number',
                'Inner loop variable represents the second number',
                'Calculate and display the product for each combination'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Programming Assignment',
          title: 'Control Flow Challenge Problems',
          points: 75,
          dueWeek: 5
        },
        {
          type: 'Practical Exercise',
          title: 'Loop Patterns and Applications',
          points: 50,
          dueWeek: 6
        }
      ]
    },
    {
      id: 'module-3',
      title: 'Functions and Modular Programming',
      description: 'Learn to organize code into reusable functions and understand the principles of modular programming design',
      weeks: [7, 8, 9],
      learningOutcomes: [
        'Define and call functions with parameters and return values',
        'Understand function scope and variable visibility',
        'Apply principles of modular programming and code organization',
        'Implement recursive functions and understand their applications'
      ],
      lessons: [
        {
          id: 'lesson-9',
          title: 'Function Basics',
          content: 'Introduction to functions as reusable blocks of code. This lesson covers function declaration, function expressions, parameters, arguments, return values, and function calling. You will learn how functions help organize code, reduce repetition, and create more maintainable programs.',
          duration: '120 minutes',
          readingMaterials: [
            'JavaScript Functions Tutorial',
            'Function Declaration vs Expression',
            'Best Practices for Function Design'
          ],
          keyTerms: [
            'Function',
            'Function Declaration',
            'Function Expression',
            'Parameter',
            'Argument',
            'Return Value',
            'Function Call',
            'Code Reusability'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a function called "greetUser" that takes a name parameter and returns a greeting message. Then call the function with "Sarah" and display the result.',
              initialCode: '// Create greetUser function and call it\n// Function should take name parameter and return greeting\n\n',
              expectedOutput: 'Hello, Sarah! Nice to meet you!',
              hints: [
                'Use function declaration: function greetUser(name) { ... }',
                'Return a string that includes the name parameter',
                'Call the function: greetUser("Sarah")',
                'Use console.log to display the returned value'
              ]
            }
          }
        },
        {
          id: 'lesson-10',
          title: 'Function Parameters and Return Values',
          content: 'Deep dive into function parameters, default parameters, rest parameters, and return statements. This lesson covers multiple parameters, parameter ordering, return value types, and functions that modify vs. functions that return new values. You will learn to design functions that are flexible and predictable.',
          duration: '120 minutes',
          readingMaterials: [
            'Advanced Function Parameters in JavaScript',
            'Return Values and Function Output',
            'Function Design Patterns'
          ],
          keyTerms: [
            'Default Parameters',
            'Rest Parameters',
            'Multiple Parameters',
            'Return Statement',
            'Pure Functions',
            'Side Effects',
            'Function Signature'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a function called "calculateArea" that takes length and width parameters and returns the area (length * width). Call it with values 8 and 5, then display the result.',
              initialCode: '// Create calculateArea function\n// Takes length and width, returns area\n// Call with 8 and 5\n\n',
              expectedOutput: 'Area: 40',
              hints: [
                'Function should multiply length by width',
                'Return the calculated area',
                'Call calculateArea(8, 5)',
                'Display result as "Area: 40"'
              ]
            }
          }
        },
        {
          id: 'lesson-11',
          title: 'Variable Scope and Closures',
          content: 'Understand how variable scope works in JavaScript functions. This lesson covers global scope, function scope, block scope, variable hoisting, and closures. You will learn how variables are accessed within different contexts and how to avoid common scope-related bugs.',
          duration: '120 minutes',
          readingMaterials: [
            'JavaScript Scope and Closures',
            'Understanding Variable Hoisting',
            'Lexical Scope in JavaScript'
          ],
          keyTerms: [
            'Global Scope',
            'Function Scope',
            'Block Scope',
            'Local Variable',
            'Global Variable',
            'Variable Hoisting',
            'Closure',
            'Lexical Scope'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a function called "counter" that demonstrates scope. Declare a variable "count" with value 0 inside the function, increment it by 1, and return the new value. Call the function twice and display both results.',
              initialCode: '// Create counter function demonstrating scope\n// Initialize count = 0, increment by 1, return value\n// Call function twice\n\n',
              expectedOutput: '1\n1',
              hints: [
                'Declare count variable inside the function',
                'Increment count by 1 each time function is called',
                'Return the incremented value',
                'Each function call creates a new scope'
              ]
            }
          }
        },
        {
          id: 'lesson-12',
          title: 'Arrow Functions and Modern Syntax',
          content: 'Learn modern JavaScript function syntax including arrow functions, concise syntax for simple functions, and differences between arrow functions and traditional functions. This lesson covers when to use arrow functions, implicit returns, and how arrow functions handle the "this" keyword.',
          duration: '90 minutes',
          readingMaterials: [
            'ES6 Arrow Functions Guide',
            'Arrow Functions vs Regular Functions',
            'Modern JavaScript Syntax'
          ],
          keyTerms: [
            'Arrow Function',
            'ES6 Syntax',
            'Implicit Return',
            'Concise Body',
            'Function Expression',
            'This Binding',
            'Modern JavaScript'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Convert this regular function to an arrow function: Create an arrow function called "double" that takes a number and returns it multiplied by 2. Call it with the value 7.',
              initialCode: '// Create arrow function "double" that multiplies by 2\n// Call with value 7 and display result\n\n',
              expectedOutput: '14',
              hints: [
                'Use arrow function syntax: const double = (num) => { ... }',
                'Return num * 2',
                'Call double(7)',
                'Display the result'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Function Design Project',
          title: 'Utility Functions Library',
          points: 100,
          dueWeek: 8
        },
        {
          type: 'Code Review Assignment',
          title: 'Function Best Practices Analysis',
          points: 50,
          dueWeek: 9
        }
      ]
    },
    {
      id: 'module-4',
      title: 'Data Structures and Arrays',
      description: 'Master arrays and basic data structures for storing and manipulating collections of data',
      weeks: [10, 11, 12],
      learningOutcomes: [
        'Create and manipulate arrays effectively',
        'Apply array methods for data processing and transformation',
        'Understand multi-dimensional arrays and nested data structures',
        'Implement basic algorithms for searching and sorting arrays'
      ],
      lessons: [
        {
          id: 'lesson-13',
          title: 'Introduction to Arrays',
          content: 'Learn about arrays as ordered collections of data. This lesson covers array creation, indexing, length property, adding and removing elements, and basic array operations. You will understand how arrays store multiple values and how to access and modify array elements efficiently.',
          duration: '120 minutes',
          readingMaterials: [
            'JavaScript Arrays Fundamentals',
            'Array Methods and Properties',
            'Working with Array Indices'
          ],
          keyTerms: [
            'Array',
            'Index',
            'Array Element',
            'Array Length',
            'Zero-based Indexing',
            'Array Literal',
            'Dynamic Array',
            'Array Bounds'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create an array called "fruits" with three elements: "apple", "banana", "orange". Display the second element (index 1) and the array length.',
              initialCode: '// Create fruits array and display information\n// Array: ["apple", "banana", "orange"]\n// Display second element and length\n\n',
              expectedOutput: 'Second fruit: banana\nArray length: 3',
              hints: [
                'Use array literal syntax with square brackets',
                'Access second element with fruits[1]',
                'Use fruits.length for array length',
                'Display both pieces of information'
              ]
            }
          }
        },
        {
          id: 'lesson-14',
          title: 'Array Methods and Manipulation',
          content: 'Explore essential array methods for adding, removing, and modifying elements. This lesson covers push(), pop(), shift(), unshift(), splice(), slice(), indexOf(), and includes(). You will learn how to manipulate arrays dynamically and choose the appropriate method for different scenarios.',
          duration: '120 minutes',
          readingMaterials: [
            'Essential JavaScript Array Methods',
            'Array Manipulation Techniques',
            'Choosing the Right Array Method'
          ],
          keyTerms: [
            'Push Method',
            'Pop Method',
            'Shift Method',
            'Unshift Method',
            'Splice Method',
            'Slice Method',
            'Array Search Methods',
            'Mutating Methods'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Start with an array [1, 2, 3]. Add the number 4 to the end, remove the first element, and display the final array.',
              initialCode: '// Start with [1, 2, 3]\n// Add 4 to end, remove first element\n// Display final array\n\n',
              expectedOutput: '[2,3,4]',
              hints: [
                'Use push() to add element to end',
                'Use shift() to remove first element',
                'Display the array after modifications',
                'Result should be [2, 3, 4]'
              ]
            }
          }
        },
        {
          id: 'lesson-15',
          title: 'Iterating Through Arrays',
          content: 'Learn different methods for iterating through arrays including for loops, for...of loops, forEach(), map(), filter(), and reduce(). This lesson covers when to use each iteration method, functional programming concepts, and how to process array data efficiently.',
          duration: '120 minutes',
          readingMaterials: [
            'Array Iteration Methods in JavaScript',
            'Functional Programming with Arrays',
            'Map, Filter, and Reduce Explained'
          ],
          keyTerms: [
            'Array Iteration',
            'For...of Loop',
            'forEach Method',
            'Map Method',
            'Filter Method',
            'Reduce Method',
            'Callback Function',
            'Functional Programming'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create an array [2, 4, 6, 8] and use a for loop to display each number doubled.',
              initialCode: '// Array: [2, 4, 6, 8]\n// Use for loop to display each number doubled\n\n',
              expectedOutput: '4\n8\n12\n16',
              hints: [
                'Create the array with values [2, 4, 6, 8]',
                'Use for loop with i from 0 to array.length',
                'Multiply each element by 2',
                'Display each doubled value'
              ]
            }
          }
        },
        {
          id: 'lesson-16',
          title: 'Multi-dimensional Arrays and Objects',
          content: 'Introduction to complex data structures including arrays of arrays and basic object manipulation. This lesson covers nested arrays, accessing multi-dimensional data, object literals, object properties, and combining arrays with objects for more sophisticated data storage.',
          duration: '120 minutes',
          readingMaterials: [
            'Multi-dimensional Arrays in JavaScript',
            'JavaScript Objects Basics',
            'Complex Data Structures'
          ],
          keyTerms: [
            'Multi-dimensional Array',
            'Nested Array',
            'Object Literal',
            'Object Property',
            'Key-Value Pair',
            'Dot Notation',
            'Bracket Notation',
            'Data Structure'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a 2D array representing a 2x2 matrix: [[1, 2], [3, 4]]. Display the element in the second row, first column (value should be 3).',
              initialCode: '// Create 2x2 matrix [[1, 2], [3, 4]]\n// Display element at second row, first column\n\n',
              expectedOutput: '3',
              hints: [
                'Create nested array with two sub-arrays',
                'Second row is index 1, first column is index 0',
                'Access using matrix[1][0]',
                'The value at that position is 3'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Array Processing Project',
          title: 'Data Analysis with Arrays',
          points: 100,
          dueWeek: 11
        },
        {
          type: 'Algorithm Implementation',
          title: 'Array Search and Sort Functions',
          points: 75,
          dueWeek: 12
        }
      ]
    },
    {
      id: 'module-5',
      title: 'Problem Solving and Final Projects',
      description: 'Apply all learned concepts to solve complex problems and build comprehensive programming projects',
      weeks: [13, 14, 15],
      learningOutcomes: [
        'Break down complex problems into manageable programming tasks',
        'Design and implement complete programming solutions',
        'Debug and test code systematically',
        'Create well-documented, maintainable code projects'
      ],
      lessons: [
        {
          id: 'lesson-17',
          title: 'Problem-Solving Methodology',
          content: 'Learn systematic approaches to solving programming problems. This lesson covers problem analysis, algorithm design, pseudocode, flowcharts, and breaking complex problems into smaller sub-problems. You will develop structured thinking skills essential for programming success.',
          duration: '120 minutes',
          readingMaterials: [
            'Problem-Solving Strategies for Programmers',
            'Algorithm Design Fundamentals',
            'Pseudocode and Flowchart Techniques'
          ],
          keyTerms: [
            'Problem Analysis',
            'Algorithm Design',
            'Pseudocode',
            'Flowchart',
            'Decomposition',
            'Top-down Design',
            'Step-wise Refinement',
            'Problem Domain'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a function that finds the largest number in an array [12, 45, 23, 67, 34, 89, 56]. Return the largest number.',
              initialCode: '// Find largest number in array\n// Array: [12, 45, 23, 67, 34, 89, 56]\n// Return the largest number\n\n',
              expectedOutput: '89',
              hints: [
                'Create a function that takes an array parameter',
                'Use a loop to compare each element',
                'Keep track of the largest number found so far',
                'Return the largest number (89)'
              ]
            }
          }
        },
        {
          id: 'lesson-18',
          title: 'Debugging and Testing',
          content: 'Master debugging techniques and testing strategies to write reliable code. This lesson covers common programming errors, debugging tools, console debugging, systematic testing approaches, and code verification methods. You will learn to identify and fix bugs efficiently.',
          duration: '120 minutes',
          readingMaterials: [
            'Debugging Techniques for Programmers',
            'Testing Strategies in Software Development',
            'Common Programming Errors and Solutions'
          ],
          keyTerms: [
            'Debugging',
            'Syntax Error',
            'Logic Error',
            'Runtime Error',
            'Test Case',
            'Bug',
            'Breakpoint',
            'Code Review'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Debug this function that should calculate the average of three numbers. Fix the error and test with values 10, 20, 30 (average should be 20).',
              initialCode: '// Debug this function\nfunction calculateAverage(a, b, c) {\n    let sum = a + b + c;\n    let average = sum / 2; // Bug is here!\n    return average;\n}\n\nlet result = calculateAverage(10, 20, 30);\nconsole.log("Average:", result);',
              expectedOutput: 'Average: 20',
              hints: [
                'The function divides by 2 instead of 3',
                'Average of three numbers should divide sum by 3',
                'Change "sum / 2" to "sum / 3"',
                'Test with values 10, 20, 30'
              ]
            }
          }
        },
        {
          id: 'lesson-19',
          title: 'Code Organization and Best Practices',
          content: 'Learn professional coding standards, code organization principles, and documentation practices. This lesson covers naming conventions, code formatting, commenting strategies, modular design, and maintainable code principles that prepare you for collaborative programming environments.',
          duration: '120 minutes',
          readingMaterials: [
            'Clean Code Principles',
            'JavaScript Style Guides',
            'Code Documentation Best Practices'
          ],
          keyTerms: [
            'Code Style',
            'Naming Conventions',
            'Code Comments',
            'Documentation',
            'Refactoring',
            'Maintainable Code',
            'Code Readability',
            'Best Practices'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Refactor this poorly written function to follow best practices. The function should convert Fahrenheit to Celsius using the formula: (F - 32) * 5/9. Test with 100°F (should return 37.78).',
              initialCode: '// Refactor this function to follow best practices\nfunction f(x) {\n    return (x-32)*5/9;\n}\n\nlet temp = f(100);\nconsole.log(temp);',
              expectedOutput: '37.77777777777778',
              hints: [
                'Give the function a descriptive name like "fahrenheitToCelsius"',
                'Use descriptive parameter names',
                'Add comments explaining the formula',
                'Format the code clearly with proper spacing'
              ]
            }
          }
        },
        {
          id: 'lesson-20',
          title: 'Final Project Integration',
          content: 'Integrate all course concepts into a comprehensive final project. This lesson guides you through planning, implementing, and presenting a complete programming solution that demonstrates mastery of variables, functions, arrays, control structures, and problem-solving skills.',
          duration: '120 minutes',
          readingMaterials: [
            'Project Planning for Programmers',
            'Software Development Lifecycle Basics',
            'Portfolio Development for Programmers'
          ],
          keyTerms: [
            'Project Planning',
            'Requirements Analysis',
            'Implementation',
            'Testing Phase',
            'Code Integration',
            'Project Documentation',
            'Portfolio Development'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a simple grade calculator that takes an array of scores [85, 92, 78, 96, 88] and returns an object with the average, highest, and lowest scores.',
              initialCode: '// Grade calculator function\n// Input: [85, 92, 78, 96, 88]\n// Return object with average, highest, lowest\n\n',
              expectedOutput: '{"average":87.8,"highest":96,"lowest":78}',
              hints: [
                'Calculate sum of all scores and divide by length for average',
                'Use Math.max() and Math.min() or loops to find highest/lowest',
                'Return an object with three properties',
                'Format the result as JSON for display'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Comprehensive Project',
          title: 'Personal Programming Portfolio',
          points: 150,
          dueWeek: 14
        },
        {
          type: 'Problem-Solving Challenge',
          title: 'Algorithm Implementation Project',
          points: 100,
          dueWeek: 15
        }
      ]
    }
  ],
  assignments: [
    {
      id: 'assignment-1',
      title: 'Variables and Basic Operations Program',
      description: 'Create a comprehensive program that demonstrates understanding of variables, data types, and basic operations. Build a personal information manager that stores and displays various types of data.',
      instructions: 'Develop a program that stores personal information (name, age, favorite number, hobbies array) and performs calculations and string manipulations to display formatted output.',
      dueWeek: 4,
      points: 100,
      submissionFormat: 'JavaScript file with comments explaining each section',
      evaluationCriteria: [
        'Correct use of variable declarations',
        'Appropriate data type usage',
        'Proper operator implementation',
        'Code organization and comments',
        'Output formatting and presentation'
      ]
    },
    {
      id: 'assignment-2',
      title: 'Control Flow and Logic Implementation',
      description: 'Design and implement programs that use conditional statements and loops to solve practical problems. Create interactive programs that respond to different inputs and conditions.',
      instructions: 'Build three mini-programs: a number guessing game with feedback, a pattern printer using loops, and a menu-driven calculator with multiple operations.',
      dueWeek: 7,
      points: 125,
      submissionFormat: 'JavaScript file with three separate programs and documentation',
      evaluationCriteria: [
        'Correct implementation of conditional logic',
        'Effective use of different loop types',
        'Program flow and user experience',
        'Error handling and input validation',
        'Code efficiency and readability'
      ]
    },
    {
      id: 'assignment-3',
      title: 'Function Design and Implementation Project',
      description: 'Create a library of utility functions that demonstrate mastery of function concepts including parameters, return values, scope, and modular programming principles.',
      instructions: 'Develop a utility library with at least 8 functions covering mathematical operations, string manipulations, array processing, and data validation.',
      dueWeek: 10,
      points: 150,
      submissionFormat: 'JavaScript library file with comprehensive documentation and test cases',
      evaluationCriteria: [
        'Function design and reusability',
        'Parameter handling and return values',
        'Code modularity and organization',
        'Documentation quality',
        'Test coverage and validation'
      ]
    },
    {
      id: 'assignment-4',
      title: 'Data Structure Manipulation Project',
      description: 'Build a comprehensive data management system using arrays and objects to store, retrieve, and manipulate complex data sets. Implement sorting, searching, and filtering functionality.',
      instructions: 'Create a student grade management system that stores student information, processes grades, calculates statistics, and generates reports using various array methods.',
      dueWeek: 13,
      points: 175,
      submissionFormat: 'Complete JavaScript application with data files and user interface',
      evaluationCriteria: [
        'Effective use of data structures',
        'Implementation of array methods',
        'Data processing and analysis',
        'User interface design',
        'Program efficiency and scalability'
      ]
    }
  ],
  finalProject: {
    id: 'final-project',
    title: 'Comprehensive Programming Application',
    description: 'Design and implement a complete programming application that integrates all course concepts including variables, functions, arrays, control structures, and problem-solving methodologies.',
    requirements: [
      'Multi-module application structure',
      'Interactive user interface with input/output',
      'Data storage and manipulation using arrays/objects',
      'Custom functions for different operations',
      'Error handling and data validation',
      'Comprehensive documentation and user guide'
    ],
    points: 250,
    dueWeek: 15,
    presentationRequired: true,
    projectOptions: [
      'Personal Finance Calculator with budgeting features',
      'Task Management System with priority and deadline tracking',
      'Simple Game with scoring and multiple levels',
      'Data Analysis Tool for processing and visualizing information',
      'Custom project approved by instructor'
    ]
  },
  finalExam: {
    id: 'final-exam',
    title: 'Comprehensive Programming Assessment',
    description: 'Comprehensive examination testing understanding of programming fundamentals, problem-solving skills, and practical application of JavaScript concepts.',
    week: 15,
    points: 200,
    timeLimit: 180,
    format: 'Mixed format: multiple choice, short answer, code writing, and problem-solving questions',
    sections: [
      {
        title: 'Programming Fundamentals and Syntax',
        points: 50,
        questions: 25
      },
      {
        title: 'Control Structures and Logic',
        points: 50,
        questions: 20
      },
      {
        title: 'Functions and Modular Programming',
        points: 50,
        questions: 20
      },
      {
        title: 'Data Structures and Arrays',
        points: 30,
        questions: 15
      },
      {
        title: 'Problem Solving and Application',
        points: 20,
        questions: 10
      }
    ]
  },
  quizzes: [
    {
      id: 'quiz-1',
      title: 'Variables and Data Types Assessment',
      description: 'Test understanding of variable declaration, data types, and basic operations',
      week: 3,
      points: 25,
      timeLimit: 30,
      questions: 15,
      topics: ['Variable declaration', 'Data types', 'Operators', 'Basic syntax']
    },
    {
      id: 'quiz-2',
      title: 'Control Structures Quiz',
      description: 'Evaluate knowledge of conditional statements and loops',
      week: 6,
      points: 30,
      timeLimit: 45,
      questions: 20,
      topics: ['If-else statements', 'Switch statements', 'For loops', 'While loops']
    },
    {
      id: 'quiz-3',
      title: 'Functions and Scope Assessment',
      description: 'Test function creation, parameters, return values, and scope understanding',
      week: 9,
      points: 35,
      timeLimit: 45,
      questions: 20,
      topics: ['Function declaration', 'Parameters and arguments', 'Return values', 'Variable scope']
    },
    {
      id: 'quiz-4',
      title: 'Arrays and Data Structures Quiz',
      description: 'Assess array manipulation and data structure understanding',
      week: 12,
      points: 30,
      timeLimit: 40,
      questions: 18,
      topics: ['Array creation', 'Array methods', 'Iteration', 'Multi-dimensional arrays']
    },
    {
      id: 'quiz-5',
      title: 'Problem Solving and Integration Quiz',
      description: 'Evaluate overall programming problem-solving abilities',
      week: 14,
      points: 35,
      timeLimit: 50,
      questions: 15,
      topics: ['Algorithm design', 'Debugging', 'Code optimization', 'Best practices']
    }
  ],
  practicalExercises: [
    {
      id: 'exercise-1',
      title: 'Basic Calculator Implementation',
      description: 'Build a calculator that performs basic arithmetic operations',
      week: 4,
      points: 25,
      estimatedTime: 120
    },
    {
      id: 'exercise-2',
      title: 'Pattern Printing with Loops',
      description: 'Create various patterns using nested loops and conditional statements',
      week: 6,
      points: 30,
      estimatedTime: 90
    },
    {
      id: 'exercise-3',
      title: 'Function Library Development',
      description: 'Create a collection of utility functions for common programming tasks',
      week: 9,
      points: 40,
      estimatedTime: 180
    },
    {
      id: 'exercise-4',
      title: 'Array Data Processing',
      description: 'Process and analyze data sets using array methods and algorithms',
      week: 12,
      points: 35,
      estimatedTime: 150
    }
  ],
  resources: [
    {
      category: 'Required Textbooks',
      items: [
        'Flanagan, D. (2020). JavaScript: The Definitive Guide (7th ed.). O\'Reilly Media.',
        'Simpson, K. (2021). You Don\'t Know JS Yet: Get Started (2nd ed.). O\'Reilly Media.',
        'Crockford, D. (2018). JavaScript: The Good Parts. O\'Reilly Media.'
      ]
    },
    {
      category: 'Online Resources',
      items: [
        'MDN Web Docs JavaScript Guide',
        'freeCodeCamp JavaScript Algorithms and Data Structures',
        'JavaScript.info - The Modern JavaScript Tutorial',
        'Codecademy JavaScript Course',
        'Khan Academy Intro to Programming'
      ]
    },
    {
      category: 'Development Tools',
      items: [
        'Visual Studio Code (Recommended IDE)',
        'Node.js for JavaScript runtime',
        'Git for version control',
        'Chrome DevTools for debugging',
        'JSFiddle for online code testing'
      ]
    },
    {
      category: 'Practice Platforms',
      items: [
        'HackerRank Programming Challenges',
        'LeetCode Beginner Problems',
        'Codewars JavaScript Kata',
        'Project Euler Mathematical Problems',
        'Exercism JavaScript Track'
      ]
    },
    {
      category: 'Reference Materials',
      items: [
        'JavaScript Cheat Sheet',
        'MDN JavaScript Reference',
        'Array Methods Quick Reference',
        'Function Syntax Guide',
        'Debugging Best Practices Checklist'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Quizzes': '15%',
    'Assignments': '35%',
    'Practical Exercises': '15%',
    'Final Project': '25%',
    'Final Exam': '10%'
  },
  attendancePolicy: 'Regular attendance is essential for success in programming. Students are expected to participate in all interactive coding exercises and lab sessions.',
  latePolicy: 'Programming assignments submitted late will be penalized 10% per day. Extensions may be granted for technical difficulties or emergencies with proper documentation.',
  accommodations: 'Students with disabilities requiring accommodations should contact the Office of Disability Services. Common accommodations include extended time for coding exercises and alternative input methods.',
  academicIntegrity: 'All code must be original work. While collaboration and discussion are encouraged, copying code from others or online sources without attribution constitutes plagiarism. Use of AI assistants should be disclosed.',
  technologyRequirements: 'Students need a computer capable of running a code editor, web browser, and Node.js. Reliable internet connection required for accessing online resources and submitting assignments.',
  softwareRequirements: [
    'Text editor (Visual Studio Code recommended)',
    'Web browser (Chrome or Firefox)',
    'Node.js runtime environment',
    'Git version control system'
  ],
  courseEvaluation: 'Student feedback collected through mid-term evaluations and final course surveys to continuously improve the learning experience and course content.'
};